import { useMemo, useState } from 'react';
import { Stack, Card, Title, Text, Group, Button, Badge, SimpleGrid, Skeleton, Anchor } from '@repletoryreach/sdk-ui';
import { Alert, EmptyState, PageHeader, StatusBadge } from '@repletoryreach/sdk-ui/data-display';
import { StatCard } from '../../../components/StatCard';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { admin } from '@repletoryreach/sdk-api';
import {
  useIntegrationProviders,
  useIntegrations,
  useTestIntegration,
  useDeleteIntegration
} from '../../../data/useIntegrations';
import { EditIntegrationDrawer } from './components/EditIntegrationDrawer';
import { SmsCard } from './components/SmsCard';
import { EmailCard } from './components/EmailCard';
import { formatDateTime, formatRelative } from '../../../lib/format';
import { useT, type Translate } from '../../../i18n/I18nProvider';
import './integrations.css';

const PlugIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M9 3v5M15 3v5" /><path d="M6 8h12v3.5a6 6 0 0 1-12 0z" /><path d="M12 17.5V21" />
  </svg>
);
const PowerIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12 3v8" /><path d="M6.5 6.5a8 8 0 1 0 11 0" />
  </svg>
);
const VerifiedIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12 3l7 3v5.5c0 4.4-3 7.9-7 9.5-4-1.6-7-5.1-7-9.5V6z" /><path d="M9 12l2.2 2.2L15.5 10" />
  </svg>
);
const FlagIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M6 21V4" /><path d="M6 4.5h11l-2 3.5 2 3.5H6" />
  </svg>
);

const ChatIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M20 12a8 8 0 0 1-11.6 7.1L4 20l.9-4.4A8 8 0 1 1 20 12z" />
  </svg>
);
const CardIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="3" y="5.5" width="18" height="13" rx="2" /><path d="M3 10h18" /><path d="M6.5 14.5h4" />
  </svg>
);
const BoxIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12 3l8 4.5v9L12 21l-8-4.5v-9z" /><path d="M4 7.5l8 4.5 8-4.5M12 12v9" />
  </svg>
);
const BellIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M6 9a6 6 0 1 1 12 0c0 5 2 6 2 6H4s2-1 2-6z" /><path d="M10 19a2 2 0 0 0 4 0" />
  </svg>
);
const MailIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="3" y="5.5" width="18" height="13" rx="2" /><path d="M3.5 7l8.5 6 8.5-6" />
  </svg>
);
const BrainIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M9 4a3 3 0 0 0-3 3 3 3 0 0 0-1 5.8V16a3 3 0 0 0 4 2.8" /><path d="M15 4a3 3 0 0 1 3 3 3 3 0 0 1 1 5.8V16a3 3 0 0 1-4 2.8" /><path d="M12 4.5v15" />
  </svg>
);

/**
 * A tile per provider category. The console ships no vendor logos, and the
 * mark on each card is what lets an operator find the one they came for in a
 * grid of eleven, so the category earns the tile instead.
 */
const CATEGORY_ICONS: Record<string, typeof PlugIcon> = {
  messaging: ChatIcon,
  payments: CardIcon,
  storage: BoxIcon,
  observability: BellIcon,
  email: MailIcon,
  llm: BrainIcon,
  compliance: VerifiedIcon
};

export default function IntegrationsPage() {
  const t = useT();
  const providersQ = useIntegrationProviders();
  const rowsQ = useIntegrations();
  const testM = useTestIntegration();
  const deleteM = useDeleteIntegration();
  const [editing, setEditing] = useState<admin.IntegrationProvider | null>(null);
  const [firingAlert, setFiringAlert] = useState(false);

  const providers = providersQ.data?.data ?? [];
  const rowsByProvider = useMemo(() => {
    const map = new Map<string, admin.Integration>();
    for (const r of rowsQ.data?.data ?? []) {
      if (r.scope_id === 1 && !r.scope_target) map.set(r.provider_code, r);
    }
    return map;
  }, [rowsQ.data]);

  /*
   * A provider can also be configured for one organisation, which overrides the
   * platform row for that tenant only. The card used to show the platform row
   * alone, so an operator reading "Connected" had no way to know four tenants
   * were pointed somewhere else entirely.
   */
  const overridesByProvider = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of rowsQ.data?.data ?? []) {
      if (r.scope_id === 1 && !r.scope_target) continue;
      map.set(r.provider_code, (map.get(r.provider_code) ?? 0) + 1);
    }
    return map;
  }, [rowsQ.data]);

  const grouped = useMemo(() => {
    const byCat = new Map<string, admin.IntegrationProvider[]>();
    for (const p of providers) {
      const list = byCat.get(p.category) ?? [];
      list.push(p);
      byCat.set(p.category, list);
    }
    return Array.from(byCat.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [providers]);

  const loading = providersQ.isLoading || rowsQ.isLoading;
  const loadError = providersQ.error || rowsQ.error;

  // What tenants need before they can do anything real: messages, media, money, email.
  const REQUIRED = ['meta_whatsapp', 'aws_s3', 'smtp'];
  const PAYMENT = ['payfast', 'stripe', 'paystack'];
  const live = (code: string) => { const r = rowsByProvider.get(code); return Boolean(r?.enabled && r.last_test_status === 'ok'); };
  const summary = {
    total: providers.length,
    enabled: providers.filter((p) => rowsByProvider.get(p.code)?.enabled).length,
    tested: providers.filter((p) => live(p.code)).length,
    missing: [...REQUIRED.filter((c) => !live(c)), ...(PAYMENT.some(live) ? [] : ['payments'])]
  };
  const [openChecks, setOpenChecks] = useState<string | null>(null);
  const runTest = (code: string, name: string) => testM.mutate(code, {
    onSuccess: (res) => {
      if (res.data.status === 'ok') notifySuccess(res.data.message, t('settings.integrations.toast.testOk', { name }));
      else notifyError(res.data.message, t('settings.integrations.toast.testFailed', { name }));
      setOpenChecks(code);
    },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('settings.integrations.toast.testFailed', { name }))
  });

  /*
   * This button reaches a person: PagerDuty triggers an incident and Slack
   * posts to the alert channel. It sat unguarded in the header of a
   * configuration page, one click from the Save buttons, so it confirms and
   * says who it will wake.
   */
  function fireTestAlert() {
    openConfirmModal({
      title: t('settings.integrations.confirmTestAlert.title'),
      children: <p style={{ margin: 0 }}>{t('settings.integrations.confirmTestAlert.body')}</p>,
      labels: { confirm: t('settings.integrations.confirmTestAlert.confirm'), cancel: t('action.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        setFiringAlert(true);
        try {
          const res = await admin.testAlert();
          const ok = res.data.channels.filter((c) => c.status === 'ok');
          if (ok.length > 0) {
            notifySuccess(
              t('settings.integrations.toast.alertFired', { channels: ok.map((c) => c.channel).join(' + ') }),
              t('settings.integrations.toast.alertFiredTitle')
            );
          } else {
            notifyError(
              t('settings.integrations.toast.noAlertChannel'),
              t('settings.integrations.toast.nothingDispatched')
            );
          }
        } catch (e) {
          notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('settings.integrations.toast.testAlertFailed'));
        } finally {
          setFiringAlert(false);
        }
      }
    });
  }

  function removeIntegration(p: admin.IntegrationProvider) {
    openConfirmModal({
      title: t('settings.integrations.remove.title'),
      children: <p style={{ margin: 0 }}>{t('settings.integrations.remove.body', { name: p.display_name })}</p>,
      labels: { confirm: t('action.remove'), cancel: t('action.cancel') },
      confirmProps: { color: 'red' },
      // Awaited: the modal used to close on a fire-and-forget mutate, so a
      // failed delete looked exactly like a successful one.
      onConfirm: async () => {
        try {
          await deleteM.mutateAsync(p.code);
          notifySuccess(t('settings.integrations.toast.removed', { name: p.display_name }), t('settings.integrations.toast.removedTitle'));
        } catch (e) {
          notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('settings.integrations.toast.removeFailed'));
        }
      }
    });
  }

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('common.settings')}
        title={t('settings.layout.nav.integrations')}
        description={t('settings.integrations.description')}
        actions={
          <Button size="sm" variant="default" loading={firingAlert} onClick={fireTestAlert}>
            {t('settings.integrations.fireTestAlert')}
          </Button>
        }
      />

      {/* Whoever can open this page can read every credential the platform
          holds. That is worth saying on the page rather than only in a
          migration comment. */}
      <Alert color="warning">{t('settings.integrations.plaintextNotice')}</Alert>

      {loadError && (
        <Alert color="error">
          <Group justify="space-between" align="center" wrap="wrap">
            <Text role="bodySmall">{t('settings.integrations.loadError')}</Text>
            <Button
              size="xs"
              variant="outline"
              onClick={() => { void providersQ.refetch(); void rowsQ.refetch(); }}
            >
              {t('action.retry')}
            </Button>
          </Group>
        </Alert>
      )}

      {loading && (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
          <Skeleton height={220} />
          <Skeleton height={220} />
          <Skeleton height={220} />
        </SimpleGrid>
      )}

      {!loading && !loadError && providers.length === 0 && (
        <EmptyState
          title={t('settings.integrations.empty.title')}
          description={t('settings.integrations.empty.description')}
        />
      )}

      {/* The old summary was one sentence with three numbers folded into it,
          which is the shape a person reads rather than scans. The same four
          facts as cards, with the readiness verdict as the fourth, so the
          answer to "can a tenant actually send anything" is a card and not a
          clause. */}
      {!loading && providers.length > 0 && (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
          <StatCard
            icon={PlugIcon}
            tone="primary"
            label={t('settings.integrations.stats.providers')}
            value={summary.total}
            hint={t('settings.integrations.stats.providersHint')}
            withRule
          />
          <StatCard
            icon={PowerIcon}
            tone="info"
            label={t('settings.integrations.stats.enabled')}
            value={summary.enabled}
            hint={t('settings.integrations.stats.enabledHint')}
            withRule
          />
          <StatCard
            icon={VerifiedIcon}
            tone={summary.tested === summary.enabled ? 'success' : 'warning'}
            label={t('settings.integrations.stats.verified')}
            value={summary.tested}
            hint={t('settings.integrations.stats.verifiedHint')}
            withRule
          />
          <StatCard
            icon={FlagIcon}
            tone={summary.missing.length === 0 ? 'success' : 'warning'}
            label={t('settings.integrations.stats.readiness')}
            value={
              <StatusBadge status={summary.missing.length === 0 ? 'healthy' : 'warning'}>
                {summary.missing.length === 0
                  ? t('settings.integrations.summary.ready')
                  : t('settings.integrations.summary.notReady')}
              </StatusBadge>
            }
            hint={
              summary.missing.length === 0
                ? t('settings.integrations.summary.hint')
                : t('settings.integrations.summary.missing', {
                    list: summary.missing
                      .map((c) => (c === 'payments'
                        ? t('settings.integrations.summary.aPaymentProvider')
                        : providers.find((p) => p.code === c)?.display_name ?? c))
                      .join(', ')
                  })
            }
            withRule
          />
        </SimpleGrid>
      )}

      {/* Customer SMS (4 October 2026): routing, the last day and a test
          send, above the provider rows that hold the keys. */}
      {!loading && providers.some((p) => p.code === 'sms_routing') && (
        <SmsCard nameOf={(code) => providers.find((p) => p.code === code)?.display_name ?? code} />
      )}

      {/* Customer email (4 October 2026): routing, the last day, suppressions and a test send. */}
      {!loading && providers.some((p) => p.code === 'email_routing') && (
        <EmailCard nameOf={(code) => providers.find((p) => p.code === code)?.display_name ?? code} />
      )}

      {grouped.map(([category, items]) => (
        <Stack gap="sm" key={category}>
          {/* Category codes come from the database; a translation is used when
              there is one and the code itself otherwise. */}
          <Text role="overline" c="var(--rr-color-text-muted)">
            {t(`settings.integrations.category.${category}`, category.toUpperCase())}
          </Text>
          {/* Two across, not three: each card carries a description, a
              consumer line, a state, a test result and four actions, and at
              three across none of those had room to be read. */}
          <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
            {items.map((p) => {
              const row = rowsByProvider.get(p.code);
              const overrides = overridesByProvider.get(p.code) ?? 0;
              return (
                <Card key={p.code} withBorder>
                  <Stack gap="md" style={{ height: '100%' }}>
                    <Group justify="space-between" align="flex-start" wrap="nowrap">
                      <Group gap="sm" align="flex-start" wrap="nowrap" style={{ minWidth: 0 }}>
                        {/* A tinted tile per category rather than a vendor logo:
                            the console ships no vendor marks, and a tile the
                            eye can land on beats an empty 40px of nothing. */}
                        <span className="rr-integrations__tile" aria-hidden>{CATEGORY_ICONS[p.category] ?? PlugIcon}</span>
                        <Stack gap="xs" style={{ minWidth: 0 }}>
                          <Title order={5}>{p.display_name}</Title>
                          <Text role="caption" c="var(--rr-color-text-muted)">{p.code}</Text>
                        </Stack>
                      </Group>
                      <Stack gap="xs" align="flex-end">
                        <StatusVariant row={row} t={t} />
                        <Text
                          role="caption"
                          c="var(--rr-color-text-muted)"
                          title={row?.last_tested_at ? formatDateTime(row.last_tested_at) : undefined}
                        >
                          {row?.last_tested_at
                            ? t('settings.integrations.testedAt', { when: formatRelative(row.last_tested_at) })
                            : t('settings.integrations.neverTested')}
                        </Text>
                      </Stack>
                    </Group>

                    <Group gap={4} wrap="wrap">
                      {REQUIRED.includes(p.code) && <Badge size="xs" variant="light" color="red">{t('settings.integrations.requiredForTenants')}</Badge>}
                      {PAYMENT.includes(p.code) && <Badge size="xs" variant="light" color="orange">{t('settings.integrations.oneOfPayments')}</Badge>}
                    </Group>

                    <Text role="bodySmall" c="var(--rr-color-text-muted)" style={{ flex: 1 }}>
                      {p.description ?? t('settings.integrations.noDescription')}
                    </Text>

                    <Group justify="space-between" align="flex-start" gap="sm" wrap="wrap" className="rr-integrations__consumers">
                      <Text role="caption" c="var(--rr-color-text-muted)" style={{ flex: 1, minWidth: 0 }}>
                        <strong>{t('settings.integrations.usedBy')}</strong> {t(`settings.integrations.usedBy.${p.code}`, t('settings.integrations.usedByUnknown'))}
                      </Text>
                      <Text role="caption" c="var(--rr-color-text-muted)" style={{ whiteSpace: 'nowrap' }}>
                        {overrides === 0
                          ? t('settings.integrations.noOrgOverrides')
                          : t(overrides === 1 ? 'settings.integrations.orgOverridesOne' : 'settings.integrations.orgOverridesOther', { count: overrides })}
                      </Text>
                    </Group>

                    <Stack gap="xs">
                      {row?.last_test_message && (
                        // A probe that asks Meta nine questions answers in nine
                        // lines, not one paragraph. The service joins them with
                        // newlines and the class below keeps them.
                        <Text
                          role="caption"
                          c={row.last_test_status === 'ok' ? 'var(--rr-color-success)' : 'var(--rr-color-error)'}
                          className="rr-integrations__result"
                        >
                          {row.last_test_message}
                        </Text>
                      )}
                      {/* `testM.data` is whichever provider was tested last, so
                          the checks belong to this card only when this card is
                          the one that ran them. */}
                      {openChecks === p.code && testM.variables === p.code
                        && testM.data?.data.checks && testM.data.data.checks.length > 0 && (
                        <ul className="rr-integrations__checks">
                          {testM.data.data.checks.map((c) => {
                            const level = checkLevel(c);
                            return (
                              <li key={c.name} data-level={level}>
                                <span aria-hidden>{level === 'fail' ? '✗' : level === 'warn' ? '!' : '✓'}</span>{' '}
                                <span><strong>{bareCheckName(c.name)}</strong> {c.detail}</span>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </Stack>

                    {/* Provenance on the left, the actions on the right, one
                        ruled row at the foot of the card, so every card ends
                        the same way and the buttons line up down the column. */}
                    <Group gap="sm" align="center" justify="space-between" wrap="wrap" className="rr-integrations__foot">
                      {row?.updated_at ? (
                        <Text role="caption" c="var(--rr-color-text-muted)" title={formatDateTime(row.updated_at)}>
                          {row.updated_by
                            ? t('settings.integrations.lastChangedBy', { when: formatRelative(row.updated_at), who: row.updated_by })
                            : t('settings.integrations.lastChanged', { when: formatRelative(row.updated_at) })}
                        </Text>
                      ) : <span />}
                      <Group gap="sm" align="center" wrap="wrap">
                      <Button size="xs" onClick={() => setEditing(p)}>
                        {row?.enabled ? t('action.edit') : row ? t('settings.integrations.configure') : t('settings.integrations.connect')}
                      </Button>
                      {row && (
                        <Button
                          size="xs"
                          variant="default"
                          loading={testM.isPending && testM.variables === p.code}
                          onClick={() => runTest(p.code, p.display_name)}
                        >
                          {t('action.test')}
                        </Button>
                      )}
                      {row && (
                        <Button
                          size="xs"
                          variant="subtle"
                          color="red"
                          loading={deleteM.isPending && deleteM.variables === p.code}
                          onClick={() => removeIntegration(p)}
                        >
                          {t('action.remove')}
                        </Button>
                      )}
                      {p.docs_url && (
                        <Anchor
                          href={p.docs_url}
                          target="_blank"
                          rel="noreferrer"
                          className="rr-integrations__docs"
                        >
                          {t('settings.integrations.docs')}
                        </Anchor>
                      )}
                      </Group>
                    </Group>
                  </Stack>
                </Card>
              );
            })}
          </SimpleGrid>
        </Stack>
      ))}

      <EditIntegrationDrawer provider={editing} onClose={() => setEditing(null)} />
    </Stack>
  );
}

function StatusVariant({ row, t }: { row: admin.Integration | undefined; t: Translate }) {
  if (!row) return <StatusBadge status="inactive">{t('status.notConfigured')}</StatusBadge>;
  if (!row.enabled) return <StatusBadge status="inactive">{t('status.disabled')}</StatusBadge>;
  if (row.last_test_status === 'ok') return <StatusBadge status="healthy">{t('settings.integrations.status.connected')}</StatusBadge>;
  if (row.last_test_status === 'error') return <StatusBadge status="warning">{t('settings.integrations.status.needsAttention')}</StatusBadge>;
  // "Enabled" and "Connected" carried a large distinction in one word each;
  // this one says what is missing.
  return <StatusBadge status="pending">{t('settings.integrations.status.enabledUntested')}</StatusBadge>;
}

/*
 * A check has three states, not two.
 *
 * `ok: false` is a refusal: the integration cannot do its job until somebody
 * fixes it. A name beginning `warn.` is the third state, which the probes use
 * for a question Meta would not answer and for an answer worth looking at
 * without being stopped by. Painting that green is how a Test button stays
 * comfortable while nothing works; painting it red is how an operator learns to
 * ignore the Test button. So it gets its own colour.
 */
function checkLevel(c: { name: string; ok: boolean }): 'ok' | 'warn' | 'fail' {
  if (!c.ok) return 'fail';
  return c.name.startsWith('warn.') ? 'warn' : 'ok';
}

/** The prefix is a wire convention, not something to read on screen. */
function bareCheckName(name: string): string {
  return name.startsWith('warn.') ? name.slice('warn.'.length) : name;
}
